import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getDeckDefinition } from './decks/registry';
import { loadDeckData } from './data/loaders';
import { parsePredictionsLocation, buildPredictionsSearch } from './url';
import SlidesView from './slides/SlidesView';
import NativeGridView from './grid/NativeGridView';
import PowerpointView from './powerpoint/PowerpointView';

const INTRO_STORAGE_KEY = 'predictions_intro_seen';

function trackPageView(path) {
  if (typeof window === 'undefined') return;
  if (typeof window.gtag !== 'function') return;
  const page_path = path || (window.location.pathname + window.location.search);
  window.gtag('event', 'page_view', { page_path });
}

function buildLegacySearch({ sector, px, pxid }) {
  const params = new URLSearchParams();

  if (sector) {
    params.append(sector, '');
  }
  if (px) params.set('px', px);
  if (pxid) params.set('pxid', pxid);

  const s = params.toString();
  return s ? `?${s}` : '';
}

function GridIframe({ deckId, sector, px, pxid }) {
  const src = useMemo(() => {
    const resolvedDeckId = deckId || '2029';
    return `/predictions/predictions_shell/${encodeURIComponent(String(resolvedDeckId))}/grid.html${buildLegacySearch({ sector, px, pxid })}`;
  }, [deckId, sector, px, pxid]);

  return (
    <iframe
      title="Predictions Grid"
      src={src}
      style={{ width: '100vw', height: '100vh', border: 'none', display: 'block' }}
    />
  );
}

function SlidesIframe({ deckId, sector, px, pxid }) {
  const src = useMemo(() => {
    const resolvedDeckId = deckId || '2029';
    return `/predictions/predictions_shell/${encodeURIComponent(String(resolvedDeckId))}/slides.html${buildLegacySearch({ sector, px, pxid })}`;
  }, [deckId, sector, px, pxid]);

  return (
    <iframe
      title="Predictions Slides"
      src={src}
      style={{ width: '100vw', height: '100vh', border: 'none', display: 'block' }}
    />
  );
}

function PredictionsApp({ deckId }) {
  const resolvedDeckId = deckId || '2029';
  const deck = getDeckDefinition(resolvedDeckId);

  const locationState = useMemo(() => parsePredictionsLocation(window.location), []);
  const [view, setView] = useState(locationState.view || 'slides');
  const [sector, setSector] = useState(locationState.sector || null);
  const [impl, setImpl] = useState(locationState.impl || 'react');
  const [px, setPx] = useState(locationState.px || null);
  const [pxid, setPxid] = useState(locationState.pxid || null);

  const [dataState, setDataState] = useState({ status: 'idle', error: null, data: null });

  const [motionDisabled, setMotionDisabled] = useState(false);
  const [introOpen, setIntroOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return !window.localStorage.getItem(INTRO_STORAGE_KEY);
    } catch (_e) {
      return true;
    }
  });
  const [slidesMenuOpen, setSlidesMenuOpen] = useState(false);
  const [pptMenuOpen, setPptMenuOpen] = useState(false);
  const slidesMenuRef = useRef(null);
  const pptMenuRef = useRef(null);
  const navRef = useRef(null);
  const orientationSwitchRef = useRef({ timer: null, lastRequest: null, lastRequestAt: 0 });
  const [navUi, setNavUi] = useState({ canPrev: false, canNext: false, canBack: false, canMit: false });
  const [isMobileView, setIsMobileView] = useState(() => {
    if (typeof window === 'undefined') return false;
    return Math.min(window.innerWidth, window.innerHeight) <= 768;
  });
  const [isPortraitMobile, setIsPortraitMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    const w = window.innerWidth;
    const h = window.innerHeight;
    return Math.min(w, h) <= 768 && h >= w;
  });

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const body = document.body;
    const html = document.documentElement;
    body.classList.add('predictions-no-scroll');
    html.classList.add('predictions-no-scroll');
    return () => {
      body.classList.remove('predictions-no-scroll');
      html.classList.remove('predictions-no-scroll');
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const updateViewportFlags = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const isMobile = Math.min(w, h) <= 768;
      setIsMobileView(isMobile);
      setIsPortraitMobile(isMobile && h >= w);
    };
    updateViewportFlags();
    window.addEventListener('resize', updateViewportFlags, { passive: true });
    window.addEventListener('orientationchange', updateViewportFlags);
    return () => {
      window.removeEventListener('resize', updateViewportFlags);
      window.removeEventListener('orientationchange', updateViewportFlags);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;

    const body = document.body;
    const html = document.documentElement;
    const scrollY = window.scrollY || window.pageYOffset || 0;

    const prevBodyStyles = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      height: body.style.height,
      overflow: body.style.overflow
    };
    const prevHtmlStyles = {
      overflow: html.style.overflow,
      height: html.style.height,
      overscrollBehaviorY: html.style.overscrollBehaviorY
    };

    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.height = '100%';
    body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';
    html.style.height = '100%';
    html.style.overscrollBehaviorY = 'none';

    const allowScrollSelector = '[data-scroll-lock="allow"]';
    const isScrollable = (target) => {
      let node = target;
      while (node && node !== document.body) {
        if (node.matches && node.matches(allowScrollSelector)) return true;
        const style = window.getComputedStyle(node);
        if ((style.overflowY === 'auto' || style.overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
          return true;
        }
        node = node.parentElement;
      }
      return false;
    };

    const onTouchMove = (event) => {
      if (!event || !event.target) return;
      if (isScrollable(event.target)) return;
      event.preventDefault();
    };

    const onWheel = (event) => {
      if (!event || !event.target) return;
      if (isScrollable(event.target)) return;
      event.preventDefault();
    };

    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('wheel', onWheel);

      body.style.position = prevBodyStyles.position;
      body.style.top = prevBodyStyles.top;
      body.style.left = prevBodyStyles.left;
      body.style.right = prevBodyStyles.right;
      body.style.width = prevBodyStyles.width;
      body.style.height = prevBodyStyles.height;
      body.style.overflow = prevBodyStyles.overflow;
      html.style.overflow = prevHtmlStyles.overflow;
      html.style.height = prevHtmlStyles.height;
      html.style.overscrollBehaviorY = prevHtmlStyles.overscrollBehaviorY;
      window.scrollTo(0, scrollY);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;

    const root = document.documentElement;
    const setViewportHeight = () => {
      const height = window.visualViewport && window.visualViewport.height
        ? window.visualViewport.height
        : window.innerHeight;
      root.style.setProperty('--pred-viewport-height', `${height}px`);
    };

    setViewportHeight();

    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', setViewportHeight, { passive: true });
      vv.addEventListener('scroll', setViewportHeight, { passive: true });
    }
    window.addEventListener('resize', setViewportHeight, { passive: true });

    return () => {
      window.removeEventListener('resize', setViewportHeight);
      if (vv) {
        vv.removeEventListener('resize', setViewportHeight);
        vv.removeEventListener('scroll', setViewportHeight);
      }
      root.style.removeProperty('--pred-viewport-height');
    };
  }, []);

  const registerNavHandlers = useCallback((handlers) => {
    navRef.current = handlers;
    if (handlers && handlers.state) {
      setNavUi(handlers.state);
    } else {
      setNavUi({ canPrev: false, canNext: false, canBack: false, canMit: false });
    }
  }, []);

  const dismissIntro = useCallback(() => {
    setIntroOpen(false);
    try {
      window.localStorage.setItem(INTRO_STORAGE_KEY, '1');
    } catch (_e) {
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.__predictionsMotionDisabled = !!motionDisabled;
    try {
      if (typeof window.predictionsGridSetMotionDisabled === 'function') {
        window.predictionsGridSetMotionDisabled(!!motionDisabled);
      }
    } catch (_e) {
    }
  }, [motionDisabled]);

  useEffect(() => {
    trackPageView();
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (!e) return;
      if (e.key !== '[' && e.key !== '{') return;

      const target = e.target;
      const targetTag = target ? target.tagName : '';
      const isTypingTarget = target && (targetTag === 'INPUT' || targetTag === 'TEXTAREA' || targetTag === 'SELECT' || target.isContentEditable);
      if (isTypingTarget) return;

      e.preventDefault();
      e.stopPropagation();
      setIntroOpen(true);
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (!e) return;

      const key = e.key;
      if (key !== 'ArrowLeft' && key !== 'ArrowRight' && key !== 'ArrowUp' && key !== 'ArrowDown') return;

      const nav = navRef.current;
      if (!nav) return;

      let fn = null;
      if (key === 'ArrowLeft') fn = nav.prev;
      else if (key === 'ArrowRight') fn = nav.next;
      else if (key === 'ArrowUp') fn = nav.back;
      else if (key === 'ArrowDown') fn = nav.mit;

      if (typeof fn !== 'function') return;

      e.preventDefault();
      e.stopPropagation();
      fn();
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, []);

  useEffect(() => {
    if (window.location.pathname === '/predictions') {
      const nextUrl = `/predictions/${resolvedDeckId}${window.location.search}`;
      window.history.replaceState({}, '', nextUrl);
      trackPageView(nextUrl);
    }

    const onPopState = () => {
      const next = parsePredictionsLocation(window.location);
      setView(next.view || 'slides');
      setSector(next.sector || null);
      setImpl(next.impl || 'react');
      setPx(next.px || null);
      setPxid(next.pxid || null);
      trackPageView();
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [resolvedDeckId]);

  useEffect(() => {
    let cancelled = false;
    setDataState({ status: 'loading', error: null, data: null });
    loadDeckData(deck)
      .then((data) => {
        if (cancelled) return;
        setDataState({ status: 'ready', error: null, data });
      })
      .catch((err) => {
        if (cancelled) return;
        setDataState({ status: 'error', error: err, data: null });
      });
    return () => {
      cancelled = true;
    };
  }, [deck]);

  const setSearch = useCallback((next) => {
    const search = buildPredictionsSearch(next);
    const nextUrl = `${window.location.pathname}${search}`;
    if (window.location.pathname + window.location.search !== nextUrl) {
      window.history.pushState({}, '', nextUrl);
      trackPageView(nextUrl);
      const parsed = parsePredictionsLocation(window.location);
      setView(parsed.view || 'slides');
      setSector(parsed.sector || null);
      setImpl(parsed.impl || 'react');
      setPx(parsed.px || null);
      setPxid(parsed.pxid || null);
    }
  }, []);

  const toggleTo = (nextView) => {
    setSearch({ view: nextView, sector, impl, px, pxid });
  };

  const sectorOptions = useMemo(() => {
    if (!dataState || dataState.status !== 'ready') return [];
    const sectors = dataState.data && dataState.data.root ? dataState.data.root.sectors : null;
    if (!Array.isArray(sectors)) return [];

    const order = ['screen', 'games', 'music', 'fashion', 'creator', 'advertising', 'ai', 'live', 'craft', 'social', 'education', 'infrastructure'];
    const orderIndex = new Map(order.map((id, idx) => [id, idx]));

    return sectors
      .map((s) => {
        if (!s) return null;
        const id = s.id ? String(s.id) : '';
        if (!id) return null;
        const label = s.name ? String(s.name) : id;
        return { id, label };
      })
      .filter(Boolean)
      .sort((a, b) => {
        const ai = orderIndex.has(a.id) ? orderIndex.get(a.id) : 999;
        const bi = orderIndex.has(b.id) ? orderIndex.get(b.id) : 999;
        if (ai !== bi) return ai - bi;
        return a.label.localeCompare(b.label);
      });
  }, [dataState]);

  useEffect(() => {
    if (!slidesMenuOpen && !pptMenuOpen) return;

    const onDocMouseDown = (e) => {
      const t = e && e.target ? e.target : null;
      if (slidesMenuRef.current && slidesMenuRef.current.contains(t)) return;
      if (pptMenuRef.current && pptMenuRef.current.contains(t)) return;
      setSlidesMenuOpen(false);
      setPptMenuOpen(false);
    };

    const onKeyDown = (e) => {
      if (!e) return;
      if (e.key === 'Escape') {
        setSlidesMenuOpen(false);
        setPptMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', onDocMouseDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [pptMenuOpen, slidesMenuOpen]);

  useEffect(() => {
    const runCheck = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      if (!w || !h) return;
      const isSmall = Math.min(w, h) <= 768;
      if (!isSmall) return;

      const wantView = w > h ? 'grid' : 'slides';
      const wantImpl = w > h ? 'native' : 'react';
      const requestKey = `${wantView}:${wantImpl}`;
      const now = Date.now();
      const { lastRequest, lastRequestAt } = orientationSwitchRef.current;

      if (lastRequest === requestKey && now - lastRequestAt < 1200) return;
      if (view === wantView && impl === wantImpl) return;

      orientationSwitchRef.current.lastRequest = requestKey;
      orientationSwitchRef.current.lastRequestAt = now;
      setSearch({ view: wantView, sector, impl: wantImpl, px, pxid });
    };

    const scheduleCheck = () => {
      if (orientationSwitchRef.current.timer) {
        window.clearTimeout(orientationSwitchRef.current.timer);
      }
      orientationSwitchRef.current.timer = window.setTimeout(() => {
        orientationSwitchRef.current.timer = null;
        runCheck();
      }, 180);
    };

    window.addEventListener('resize', scheduleCheck);
    window.addEventListener('orientationchange', scheduleCheck);
    scheduleCheck();

    return () => {
      window.removeEventListener('resize', scheduleCheck);
      window.removeEventListener('orientationchange', scheduleCheck);
      if (orientationSwitchRef.current.timer) {
        window.clearTimeout(orientationSwitchRef.current.timer);
        orientationSwitchRef.current.timer = null;
      }
    };
  }, [impl, px, pxid, sector, setSearch, view]);

  return (
    <div style={{ width: '100vw', height: '100vh', background: '#000', overflow: 'hidden' }}>
      <div
        style={{
          position: 'fixed',
          top: isMobileView ? 10 : 20,
          left: isMobileView ? 10 : 20,
          right: isMobileView ? 10 : 20,
          zIndex: 2000,
          display: 'flex',
          flexDirection: 'row',
          gap: isMobileView ? 10 : 16,
          alignItems: 'center',
          justifyContent: 'space-between',
          background: isPortraitMobile ? '#000' : 'transparent',
          padding: isPortraitMobile ? '4px 6px' : 0,
          border: isPortraitMobile ? '1px solid rgba(255,255,0,0.65)' : 'none',
          boxShadow: isPortraitMobile ? '0 0 10px rgba(255, 255, 0, 0.12)' : 'none'
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: isMobileView ? 8 : 'clamp(12px, 1.6vw, 20px)',
            alignItems: 'center',
            flex: isPortraitMobile ? '1 1 auto' : '0 0 auto',
            minWidth: 0,
            background: isPortraitMobile ? 'transparent' : '#000',
            padding: isPortraitMobile ? 0 : isMobileView ? '4px 6px' : '4px 8px',
            border: isPortraitMobile ? 'none' : '1px solid rgba(255,255,0,0.65)',
            boxShadow: isPortraitMobile ? 'none' : '0 0 10px rgba(255, 255, 0, 0.12)'
          }}
        >
        <a
          href="https://obsolete.com"
          target="_blank"
          rel="noreferrer"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
        >
          <img
            src={`${deck.assetsBaseUrl}images/Obsolete.svg`}
            alt="Obsolete"
            style={{ height: isMobileView ? '26px' : 'clamp(31px, 4.7vh, 52px)', width: 'auto', display: 'block' }}
          />
        </a>

        <a
          href="https://eventus.do"
          target="_blank"
          rel="noreferrer"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
        >
          <img
            src={`${deck.assetsBaseUrl}images/eventus.svg`}
            alt="Eventus"
            style={{ height: isMobileView ? '7px' : 'clamp(9px, 1.35vh, 14px)', width: 'auto', display: 'block', transform: 'translateY(32%)' }}
          />
        </a>

        <a
          href="https://www.thefifthsector.co.uk/"
          target="_blank"
          rel="noreferrer"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
        >
          <img
            src={`${deck.assetsBaseUrl}images/the_fifth_sector_logo.png`}
            alt="The Fifth Sector"
            style={{ height: isMobileView ? '28px' : 'clamp(35px, 5.2vh, 56px)', width: 'auto', maxWidth: isMobileView ? '160px' : 'clamp(218px, 31vw, 374px)', objectFit: 'contain', display: 'block' }}
          />
        </a>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: isMobileView ? 4 : 6,
          alignItems: 'center',
          background: isPortraitMobile ? 'transparent' : '#000',
          padding: isPortraitMobile ? 0 : isMobileView ? '4px 6px' : '4px 8px',
          border: isPortraitMobile ? 'none' : '1px solid rgba(255,255,0,0.65)',
          boxShadow: isPortraitMobile ? 'none' : '0 0 10px rgba(255, 255, 0, 0.12)'
        }}
      >
        <button
          type="button"
          onClick={() => setMotionDisabled((v) => !v)}
          style={{
            border: motionDisabled ? '1px solid rgba(255,77,77,0.7)' : '1px solid rgba(255,255,0,0.7)',
            background: 'transparent',
            color: motionDisabled ? 'rgba(255,77,77,0.95)' : '#fff',
            fontFamily: 'Courier New, monospace',
            fontSize: isMobileView ? '0.55rem' : 'clamp(10px, 0.9vw, 12px)',
            fontWeight: 800,
            padding: isMobileView ? '3px 6px' : '3px 9px',
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            cursor: 'pointer',
            transition: 'all 0.15s'
          }}
        >
          MOTION
        </button>

        {!isMobileView && (
          <button
            type="button"
            onClick={() => setSearch({ view: 'grid', sector, impl: impl === 'iframe' ? 'iframe' : 'native', px, pxid })}
            disabled={view === 'grid'}
            style={{
              border: view === 'grid' ? '1px solid rgba(255,255,255,0.25)' : '1px solid #ffff00',
              background: 'transparent',
              color: view === 'grid' ? 'rgba(255,255,255,0.35)' : '#fff',
              fontFamily: 'Courier New, monospace',
              fontSize: 'clamp(10px, 0.9vw, 12px)',
              fontWeight: 700,
              padding: '3px 9px',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              cursor: view === 'grid' ? 'default' : 'pointer',
              transition: 'all 0.15s'
            }}
          >
            GRID
          </button>
        )}

        <div ref={slidesMenuRef} style={{ position: 'relative', display: 'flex', flexDirection: 'row', gap: 0 }}>
          <button
            type="button"
            onClick={() => {
              setSlidesMenuOpen(false);
              setPptMenuOpen(false);
              setSearch({ view: 'slides', sector: null, impl: 'react', px: null, pxid: null });
            }}
            style={{
              border: '1px solid #ffff00',
              borderRight: 'none',
              background: view === 'slides' ? 'rgba(255,255,0,0.08)' : 'transparent',
              color: '#fff',
              fontFamily: 'Courier New, monospace',
              fontSize: isMobileView ? '0.55rem' : 'clamp(10px, 0.9vw, 12px)',
              fontWeight: 700,
              padding: isMobileView ? '3px 6px' : '3px 9px',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              cursor: 'pointer',
              transition: 'all 0.15s'
            }}
          >
            SLIDES
          </button>
          <button
            type="button"
            aria-label="Open slides sector menu"
            onClick={() => {
              setSlidesMenuOpen((v) => !v);
              setPptMenuOpen(false);
            }}
            style={{
              border: '1px solid #ffff00',
              background: slidesMenuOpen ? 'rgba(255,255,0,0.10)' : 'transparent',
              color: '#fff',
              fontFamily: 'Courier New, monospace',
              fontSize: isMobileView ? '0.55rem' : 'clamp(10px, 0.9vw, 12px)',
              fontWeight: 700,
              padding: isMobileView ? '3px 6px' : '3px 7px',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              cursor: 'pointer',
              transition: 'all 0.15s'
            }}
          >
            ▾
          </button>

          {slidesMenuOpen && (
            <div
              style={{
                position: 'absolute',
                top: 'calc(100% + 6px)',
                right: 0,
                minWidth: 220,
                maxHeight: 'min(60vh, 440px)',
                overflowY: 'auto',
                background: 'rgba(0,0,0,0.92)',
                border: '1px solid rgba(255,255,0,0.65)',
                boxShadow: '0 0 14px rgba(255, 255, 0, 0.12)',
                padding: 6
              }}
            >
              {sectorOptions.map((s) => (
                <button
                  key={`slides-${s.id}`}
                  type="button"
                  onClick={() => {
                    setSlidesMenuOpen(false);
                    setSearch({ view: 'slides', sector: s.id, impl: 'react', px: null, pxid: null });
                  }}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    border: '1px solid rgba(255,255,0,0.25)',
                    background: sector === s.id && view === 'slides' ? 'rgba(255,255,0,0.10)' : 'transparent',
                    color: '#fff',
                    fontFamily: 'Courier New, monospace',
                    fontSize: 'clamp(10px, 0.9vw, 12px)',
                    fontWeight: 700,
                    padding: '6px 10px',
                    letterSpacing: '0.4px',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    marginBottom: 4
                  }}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </div>
        {!isMobileView && (
          <div ref={pptMenuRef} style={{ position: 'relative', display: 'flex', flexDirection: 'row', gap: 0 }}>
            <button
              type="button"
              onClick={() => {
                setPptMenuOpen(false);
                setSlidesMenuOpen(false);
                setSearch({ view: 'powerpoint', sector: null, impl: 'react', px: null, pxid: null });
              }}
              style={{
                border: '1px solid #ffff00',
                borderRight: 'none',
                background: view === 'powerpoint' ? 'rgba(255,255,0,0.08)' : 'transparent',
                color: '#fff',
                fontFamily: 'Courier New, monospace',
                fontSize: 'clamp(10px, 0.9vw, 12px)',
                fontWeight: 700,
                padding: '3px 9px',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                cursor: 'pointer',
                transition: 'all 0.15s'
              }}
            >
              PPT
            </button>
            <button
              type="button"
              aria-label="Open powerpoint sector menu"
              onClick={() => {
                setPptMenuOpen((v) => !v);
                setSlidesMenuOpen(false);
              }}
              style={{
                border: '1px solid #ffff00',
                background: pptMenuOpen ? 'rgba(255,255,0,0.10)' : 'transparent',
                color: '#fff',
                fontFamily: 'Courier New, monospace',
                fontSize: 'clamp(10px, 0.9vw, 12px)',
                fontWeight: 700,
                padding: '3px 7px',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                cursor: 'pointer',
                transition: 'all 0.15s'
              }}
            >
              ▾
            </button>

            {pptMenuOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 6px)',
                  right: 0,
                  minWidth: 220,
                  maxHeight: 'min(60vh, 440px)',
                  overflowY: 'auto',
                  background: 'rgba(0,0,0,0.92)',
                  border: '1px solid rgba(255,255,0,0.65)',
                  boxShadow: '0 0 14px rgba(255, 255, 0, 0.12)',
                  padding: 6
                }}
              >
                {sectorOptions.map((s) => (
                  <button
                    key={`ppt-${s.id}`}
                    type="button"
                    onClick={() => {
                      setPptMenuOpen(false);
                      setSearch({ view: 'powerpoint', sector: s.id, impl: 'react', px: null, pxid: null });
                    }}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      border: '1px solid rgba(255,255,0,0.25)',
                      background: sector === s.id && view === 'powerpoint' ? 'rgba(255,255,0,0.10)' : 'transparent',
                      color: '#fff',
                      fontFamily: 'Courier New, monospace',
                      fontSize: 'clamp(10px, 0.9vw, 12px)',
                      fontWeight: 700,
                      padding: '6px 10px',
                      letterSpacing: '0.4px',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      marginBottom: 4
                    }}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        </div>
      </div>

      {!isMobileView && (
        <div
          style={{
            position: 'fixed',
            right: 20,
            bottom: 20,
            zIndex: 2000,
            display: 'flex',
            gap: 6,
            background: '#000',
            padding: 6,
            border: '2px solid #ffff00',
            boxShadow: '0 0 12px rgba(255, 255, 0, 0.2)'
          }}
        >
        <button
          type="button"
          onClick={() => {
            const nav = navRef.current;
            if (nav && typeof nav.prev === 'function') nav.prev();
          }}
          disabled={!navUi.canPrev}
          style={{
            border: !navUi.canPrev ? '1px solid rgba(255,255,255,0.25)' : '1px solid #ffff00',
            background: '#000',
            color: !navUi.canPrev ? 'rgba(255,255,255,0.35)' : '#fff',
            fontFamily: 'Courier New, monospace',
            fontSize: '0.8rem',
            fontWeight: 900,
            padding: '4px 10px',
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            cursor: !navUi.canPrev ? 'default' : 'pointer',
            transition: 'all 0.15s'
          }}
        >
          ◄
        </button>

        <button
          type="button"
          onClick={() => {
            const nav = navRef.current;
            if (nav && typeof nav.back === 'function') nav.back();
          }}
          disabled={!navUi.canBack}
          style={{
            border: !navUi.canBack ? '1px solid rgba(255,255,255,0.25)' : '1px solid #ffff00',
            background: '#000',
            color: !navUi.canBack ? 'rgba(255,255,255,0.35)' : '#fff',
            fontFamily: 'Courier New, monospace',
            fontSize: '0.7rem',
            fontWeight: 900,
            padding: '4px 10px',
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            cursor: !navUi.canBack ? 'default' : 'pointer',
            transition: 'all 0.15s'
          }}
        >
          ▲ BACK
        </button>

        <button
          type="button"
          onClick={() => {
            const nav = navRef.current;
            if (nav && typeof nav.mit === 'function') nav.mit();
          }}
          disabled={!navUi.canMit}
          style={{
            border: !navUi.canMit ? '1px solid rgba(255,255,255,0.25)' : '1px solid #ffff00',
            background: '#000',
            color: !navUi.canMit ? 'rgba(255,255,255,0.35)' : '#fff',
            fontFamily: 'Courier New, monospace',
            fontSize: '0.7rem',
            fontWeight: 900,
            padding: '4px 10px',
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            cursor: !navUi.canMit ? 'default' : 'pointer',
            transition: 'all 0.15s'
          }}
        >
          ▼ MIT
        </button>

        <button
          type="button"
          onClick={() => {
            const nav = navRef.current;
            if (nav && typeof nav.next === 'function') nav.next();
          }}
          disabled={!navUi.canNext}
          style={{
            border: !navUi.canNext ? '1px solid rgba(255,255,255,0.25)' : '1px solid #ffff00',
            background: '#000',
            color: !navUi.canNext ? 'rgba(255,255,255,0.35)' : '#fff',
            fontFamily: 'Courier New, monospace',
            fontSize: '0.8rem',
            fontWeight: 900,
            padding: '4px 10px',
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            cursor: !navUi.canNext ? 'default' : 'pointer',
            transition: 'all 0.15s'
          }}
        >
          ►
        </button>
        </div>
      )}

      {view === 'slides' && impl === 'react' ? (
        <SlidesView deckId={resolvedDeckId} sector={sector} dataState={dataState} assetsBaseUrl={deck.assetsBaseUrl} registerNavHandlers={registerNavHandlers} motionDisabled={motionDisabled} />
      ) : view === 'powerpoint' && impl === 'react' ? (
        <PowerpointView deckId={resolvedDeckId} sector={sector} dataState={dataState} assetsBaseUrl={deck.assetsBaseUrl} registerNavHandlers={registerNavHandlers} motionDisabled={motionDisabled} />
      ) : view === 'slides' ? (
        <SlidesIframe deckId={resolvedDeckId} sector={sector} px={px} pxid={pxid} />
      ) : view === 'grid' && impl === 'native' ? (
        <NativeGridView deckId={resolvedDeckId} sector={sector} px={px} pxid={pxid} dataUrls={deck.dataUrls} assetsBaseUrl={deck.assetsBaseUrl} impl="native" pixelPresets={dataState && dataState.data ? dataState.data.pixelPresets : null} gridTakeoverStatements={deck.gridTakeoverStatements} registerNavHandlers={registerNavHandlers} motionDisabled={motionDisabled} />
      ) : view === 'grid' && impl === 'react' ? (
        <NativeGridView deckId={resolvedDeckId} sector={sector} px={px} pxid={pxid} dataUrls={deck.dataUrls} assetsBaseUrl={deck.assetsBaseUrl} impl="react" pixelPresets={dataState && dataState.data ? dataState.data.pixelPresets : null} gridTakeoverStatements={deck.gridTakeoverStatements} registerNavHandlers={registerNavHandlers} motionDisabled={motionDisabled} />
      ) : (
        <GridIframe deckId={resolvedDeckId} sector={sector} px={px} pxid={pxid} />
      )}

      {introOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 4000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isMobileView ? 16 : 24,
            background: 'rgba(0,0,0,0.6)'
          }}
        >
          <div
            style={{
              width: 'min(92vw, 560px)',
              background: '#000',
              border: '2px solid #ffff00',
              borderRadius: 12,
              padding: isMobileView ? '16px 18px' : '18px 20px',
              color: '#fff',
              fontFamily: 'Courier New, monospace',
              boxShadow: '0 0 20px rgba(255,255,0,0.22)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
              <div style={{ fontSize: isMobileView ? 12 : 14, fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase', color: '#ffff00' }}>
                Welcome to 2029
              </div>
              <button
                type="button"
                onClick={dismissIntro}
                style={{
                  border: '1px solid #ffff00',
                  background: 'transparent',
                  color: '#fff',
                  fontFamily: 'Courier New, monospace',
                  fontSize: isMobileView ? '0.55rem' : '0.65rem',
                  fontWeight: 700,
                  padding: '4px 8px',
                  letterSpacing: '0.6px',
                  textTransform: 'uppercase',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
            <div style={{ fontSize: isMobileView ? 11 : 12, lineHeight: 1.5, letterSpacing: '0.2px' }}>
              <div style={{ marginBottom: 8 }}>
                Top-right controls: Motion calms the chaos. PPT is clean, Slides are 90s loud, Grid is bonkers.
              </div>
              <div style={{ marginBottom: 8 }}>
                Use the Slides/PPT dropdowns to jump between sector stories.
              </div>
              <div>
                Bottom-left arrows: ◄ ► move, ▲ up a level, ▼ mitigations — what we can do now to avoid the bleak path.
              </div>
            </div>
            <div style={{ marginTop: 10, fontSize: 10, opacity: 0.7, letterSpacing: '0.3px' }}>
              Press [ or {'{'} to reopen.
            </div>
          </div>
        </div>
      )}

      {dataState.status === 'error' && (
        <div style={{ position: 'fixed', left: 16, bottom: 16, zIndex: 1000, color: '#ff4d4d', fontFamily: 'Courier New, monospace', fontSize: 12 }}>
          Failed to preload deck data: {String(dataState.error && dataState.error.message ? dataState.error.message : dataState.error)}
        </div>
      )}
    </div>
  );
}

export default PredictionsApp;
